import api from './api';

/**
 * Obtiene artículos con filtros y paginación
 * @param {Object} filters - Filtros y opciones de paginación/ordenamiento
 * @returns {Promise} - Promesa con los artículos
 */
export const getArticles = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Añadir todos los parámetros a la URL
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });
        
        const response = await api.get(`/api/articles?${queryParams}`);
        return response.data;
    } catch (error) {
        console.error('Error en servicio getArticles:', error);
        throw error;
    }
};

/**
 * Obtiene un artículo por ID
 * @param {Number} id - ID del artículo
 * @param {Object} filters - Filtros opcionales (startDate, endDate)
 * @returns {Promise} - Promesa con el artículo
 */
export const getArticleById = async (id, filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Añadir filtros de fecha si se proporcionan
        if (filters.startDate) {
            queryParams.append('startDate', filters.startDate);
        }
        if (filters.endDate) {
            queryParams.append('endDate', filters.endDate);
        }
        
        const queryString = queryParams.toString();
        const url = queryString ? `/api/articles/${id}?${queryString}` : `/api/articles/${id}`;
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio getArticleById para id ${id}:`, error);
        throw error;
    }
};

/**
 * Actualiza un artículo existente
 * @param {Number} id - ID del artículo a actualizar
 * @param {Object} articleData - Datos actualizados del artículo
 * @returns {Promise} - Promesa con el artículo actualizado
 */
export const updateArticle = async (id, articleData) => {
    try {
        const response = await api.put(`/api/articles/${id}`, articleData);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio updateArticle para id ${id}:`, error);
        throw error;
    }
};

/**
 * Recalcula las estadísticas de un artículo
 * @param {Number} id - ID del artículo
 * @returns {Promise} - Promesa con el resultado
 */
export const recalculateArticleStats = async (id) => {
    try {
        const response = await api.post(`/api/articles/${id}/recalculate`);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio recalculateArticleStats para id ${id}:`, error);
        throw error;
    }
};

/**
 * Obtiene estadísticas globales de artículos
 * @returns {Promise} - Promesa con las estadísticas globales
 */
export const getArticleStats = async () => {
    try {
        const response = await api.get('/api/articles/stats');
        return response.data;
    } catch (error) {
        console.error('Error en servicio getArticleStats:', error);
        throw error;
    }
};

/**
 * Obtiene los top artículos
 * @param {Object} params - Parámetros de filtrado (limit, sortBy, timeframe)
 * @returns {Promise} - Promesa con los top artículos
 */
export const getTopArticles = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });
        
        const response = await api.get(`/api/articles/top?${queryParams}`);
        return response.data;
    } catch (error) {
        console.error('Error en servicio getTopArticles:', error);
        throw error;
    }
};

// Hacer merge de artículos
export const mergeArticles = async (targetArticleId, sourceArticleIds) => {
    try {
        const response = await api.post('/api/articles/merge', {
            targetArticleId,
            sourceArticleIds
        });
        return response.data;
    } catch (error) {
        console.error('Error en merge de artículos:', error);
        throw error;
    }
};

// Recrear todos los artículos
export const recreateAllArticles = async () => {
    try {
        const response = await api.post(`/api/articles/recreate-all`);
        return response.data;
    } catch (error) {
        console.error('Error recreando artículos:', error);
        throw error;
    }
};

// Actualización masiva de artículos
export const updateArticlesBulk = async (articleIds, updateData) => {
    try {
        const response = await api.put('/api/articles/bulk-update', {
            articleIds,
            updateData
        });
        return response.data;
    } catch (error) {
        console.error('Error en actualización masiva de artículos:', error);
        throw error;
    }
};

// Eliminación masiva de artículos
export const deleteArticlesBulk = async (articleIds) => {
    try {
        const response = await api.delete('/api/articles/bulk-delete', {
            data: { articleIds }
        });
        return response.data;
    } catch (error) {
        console.error('Error en eliminación masiva de artículos:', error);
        throw error;
    }
};

// Buscar artículos huérfanos (sin ventas)
export const findOrphanedArticles = async () => {
    try {
        const response = await api.get('/api/articles/orphaned');
        return response.data;
    } catch (error) {
        console.error('Error buscando artículos huérfanos:', error);
        throw error;
    }
};

// Obtener tipos disponibles para artículos
export const getArticleTypes = async () => {
    try {
        const response = await api.get('/api/articles/types');
        return response.data;
    } catch (error) {
        console.error('Error obteniendo tipos de artículos:', error);
        throw error;
    }
};

export const articleService = {
    getArticles,
    getArticleById,
    updateArticle,
    recalculateArticleStats,
    getArticleStats,
    getTopArticles,
    mergeArticles,
    recreateAllArticles,
    updateArticlesBulk,
    deleteArticlesBulk,
    findOrphanedArticles,
    getArticleTypes
};

export default articleService; 